import React, { memo, useCallback } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import sanitizeHtml from 'sanitize-html';
import { useTheme, Text, Badge } from '@Sage/hoi-poi-ui';
import { CrudActions, DetailActions } from 'actions';
import { CancelIcon, IconArrow, BillboardDocumentExtensionIcon } from 'components/icons';
import Context from 'managers/Context';
import { ButtonPrimaryBig } from 'components/buttons';
import PreviewTwoStepButton from './previewTwoStepButton';
import Scrollbars from 'components/ScrollBar';
import { BILLBOARD } from 'constants/Entities';
import { getLiteral, getLiteralWithParameters } from 'utils/getLiteral';
import './styles.scss';

const mapStateToProps = (state) => {
    let files =
        state.crud.entityCrud.get('id') && state.crud.entityCrud
            ? state.crud.entityCrud.get('attachmentfiles')
            : state.billboard.crud.get('files');
    files = files ? files : [];
    return {
        entityCrud: state.crud.entityCrud,
        environmentsList: state.valueList.get('tblSucursalesBillboard').get('data'),
        loggedUserName: `${state.config.userData.nombre} ${state.config.userData.apellidos}`,
        files: files,
        literals: state.literal.literals,
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        togglePreview: bindActionCreators(CrudActions, dispatch).togglePreview,
        newDetailTab: bindActionCreators(DetailActions, dispatch).newDetailTab,
        onSave: bindActionCreators(CrudActions, dispatch).saveCrud,
    };
};

const BillboardCrudPreview = ({
    togglePreview,
    newDetailTab,
    environmentsList,
    onSave,
    entityCrud,
    loggedUserName,
    files,
    literals,
}) => {
    const theme = useTheme();

    const closePreview = useCallback(() => {
        if (entityCrud.get('id')) {
            newDetailTab && newDetailTab(BILLBOARD, entityCrud.get('id'));
        }
        togglePreview && togglePreview();
    }, [entityCrud, newDetailTab, togglePreview]);

    const getExtension = useCallback((file) => {
        let extension = file.split('.');
        return extension[extension.length - 1];
    }, []);

    const renderFile = useCallback((filename, showName, extension, size, index) => {
        return (
            <div key={filename + index} className="file-box">
                <span className="file-extension">
                    <BillboardDocumentExtensionIcon extension={extension.toUpperCase()} />
                </span>
                <div className="file-info">
                    <div className="file-name">{showName}</div>
                    {size && size !== '0' ? (
                        <div className="file-size">{Context.utilsFormats.formatFileSize(size)}</div>
                    ) : null}
                </div>
            </div>
        );
    }, []);

    const renderContentLeft = useCallback(() => {
        const environmentsListJson = environmentsList.toJSON();
        let environments = entityCrud.get('environmentsid');
        environments = environments.split(';');
        environments = environments.filter((v) => v !== '');
        let environmentsChips = environments.map((environmentId) => {
            let environment = environmentsListJson.filter((environment) => {
                return environment['-id'] === environmentId;
            });
            if (environment && environment.length === 1) {
                return (
                    <Badge key={environmentId} type="info" className="preview-content-chips__text">
                        {environment[0].descripcion}
                    </Badge>
                );
            } else {
                return null;
            }
        });
        let button;
        if (entityCrud.get('id')) {
            // Second Step validation
            button = (
                <PreviewTwoStepButton classes={['preview-confirm']} onClick={onSave}>
                    {getLiteral('action_billboard_resend')}
                </PreviewTwoStepButton>
            );
        } else {
            button = (
                <ButtonPrimaryBig classes={['preview-confirm']} onClick={onSave}>
                    {getLiteral('action_billboard_post')}
                </ButtonPrimaryBig>
            );
        }

        return (
            <div className="preview-content-left-cell">
                <div className="preview-content-title">
                    <Text type="h6" color={theme.colors.primary.white}>
                        {getLiteral('title_billboard_preview')}
                    </Text>
                    <div className="title-separator">
                        <div className="border-separator" />
                    </div>
                </div>
                <div className="preview-content-subtitle">
                    <Text type="body1" color={theme.colors.primary.white}>
                        {getLiteral('label_billboard_preview')}
                    </Text>
                </div>
                <div className="preview-content-chips">{environmentsChips}</div>
                {button}
            </div>
        );
    }, [entityCrud, environmentsList, onSave, theme.colors.primary.white]);

    const renderContentRight = useCallback(() => {
        let numFiles = files ? files.size : 0;
        let literalForFiles =
            numFiles === 1 ? 'label_billboard_attached' : 'label_billboard_attached';
        let filesRender = files.map((file, index) => {
            let extension;
            let extensionLength;
            let finalLength;
            let showName;
            if (entityCrud.get('id')) {
                extension = getExtension(file.name);
                let name = `${file.description}.${extension}`;
                showName = `${name.substring(0, 3)}...${name.substring(
                    name.length - 8,
                    name.length,
                )}`;
                return renderFile(file.description, showName, extension, file.size, index);
            } else {
                extension = getExtension(file.name);
                showName = `${file.name.substring(0, 3)}...${file.name.substring(
                    file.name.length - 8,
                    file.name.length,
                )}`;
                return renderFile(file.name, showName, extension, file.size, index);
            }
        });

        let literalForMobileConfirm =
            literals && literals.hasOwnProperty('label_billboard_confirm_meaning')
                ? literals['label_billboard_confirm_meaning']
                : 'label_billboard_confirm_meaning';
        if (literalForMobileConfirm.indexOf('%@')) {
            literalForMobileConfirm = literalForMobileConfirm.replace('%@', loggedUserName);
        }

        let descriptionClasses = ['mobile-description'];
        if (files.size === 0) {
            descriptionClasses.push('extra-large');
        }
        return (
            <div className="preview-content-right-cell">
                <div className="preview-mobile">
                    <div className="preview-mobile-content">
                        <div className="mobile-header">
                            <Text type="caption" color={theme.colors.utility.textSecondary}>
                                {`${getLiteral('label_from')}: ${loggedUserName}`}
                            </Text>
                            <div className="mobile-title">{entityCrud.get('subject')}</div>
                            <div className="mobile-date-and-files">
                                <div className="mobile-date">
                                    <div>{Context.utilsFormats.getNowMoment('LT')}</div>
                                </div>
                                <div className="mobile-files">
                                    <Text type="caption" color={theme.colors.utility.textSecondary}>
                                        {getLiteralWithParameters(literalForFiles, [
                                            numFiles.toString(),
                                        ])}
                                    </Text>
                                    <i className="ic-attachment" />
                                </div>
                            </div>
                        </div>
                        {files.size > 0 && (
                            <div className="mobile-files-list">
                                <Scrollbars autoHide={true} hideTracksWhenNotNeeded={true}>
                                    {filesRender}
                                </Scrollbars>
                            </div>
                        )}
                        <div className={descriptionClasses.join(' ')}>
                            <Scrollbars autoHide={true} hideTracksWhenNotNeeded={true}>
                                <div
                                    className="ql-editor mobile-description-text"
                                    dangerouslySetInnerHTML={{
                                        __html: sanitizeHtml(entityCrud.get('description')),
                                    }}
                                />
                            </Scrollbars>
                        </div>
                    </div>
                </div>
            </div>
        );
    }, [entityCrud, files, getExtension, literals, loggedUserName, renderFile, theme]);

    const renderHeader = useCallback(() => {
        return (
            <div className="preview-header">
                <div className="preview-header-close">
                    <CancelIcon onClick={closePreview} />
                </div>

                <Text
                    className="preview-header-main-title"
                    type="body1"
                    color={theme.colors.primary.white}
                >
                    {getLiteral('title_billboard_preview')}
                </Text>

                <div className="preview-header-arrow">
                    <IconArrow />
                </div>

                <Text
                    className="preview-header-main-title"
                    type="body1"
                    color={theme.colors.primary.white}
                >
                    {entityCrud.get('subject')}
                </Text>
            </div>
        );
    }, [closePreview, entityCrud, theme.colors.primary.white]);

    const renderContent = useCallback(() => {
        return (
            <div className="preview-content">
                <div className="preview-content-left">{renderContentLeft()}</div>
                <div className="preview-content-right">{renderContentRight()}</div>
            </div>
        );
    }, [renderContentLeft, renderContentRight]);

    return (
        <div className="billboard-crud-preview">
            {renderHeader()}
            {renderContent()}
        </div>
    );
};

export default memo(connect(mapStateToProps, mapDispatchToProps)(BillboardCrudPreview));
